import { useQuery } from '@apollo/client/react';
import { useCompanyFeatures } from 'app/domains/companies/features/CompanyFeatures';
import { getMetadataPropertyFilter, AllowedTeamsFilter, allowedTeamsFilterLabel, AllowedUsersFilter, allowedUsersFilterLabel, ContractCreatorFilter, contractCreatorFilterLabel, CreatedAtFilter, createdAtFilterLabel, DateSignedFilter, dateSignedFilterLabel, DocumentProjectsFilter, documentProjectsFilterLabel, DocumentStatusFilter, documentStatusFilterLabel, EsigningStatusFilter, esigningStatusFilterLabel, FilesystemPathFilter, fileSystemPathFilterLabel, SearchDocumentTypeFilter, searchDocumentTypeFilterLabel, SharedExternallyFilter, sharedExternallyFilterLabel, TemplateCategoryFilter, templateCategoryFilterLabel, UpdatedAtFilter, updatedAtFilterLabel, } from 'app/domains/global-search';
import { metadataCategories as metadataCategoriesConfig } from 'shared/domains/metadata/utils/metadataCategories';
import { DefaultSearchFiltersDocument } from './DefaultSearchFilters.gql';
const dmsToSearchName = (name) => `metadataDocument${name}`;
export function useGetDefaultSearchFilters() {
    const { data, error, loading: queryLoading } = useQuery(DefaultSearchFiltersDocument);
    const { metadataCategories, documentMetadataProperties } = data || {};
    const { isFeatureAvailable, loading: companyFeaturesLoading } = useCompanyFeatures();
    const isProjectsAvailable = isFeatureAvailable('PROJECTS');
    const loading = queryLoading || companyFeaturesLoading;
    const categories = metadataCategoriesConfig();
    const DOCUMENT_DETAILS_CATEGORY = '726144ab-b785-4f7e-bd32-5b38f7d5dcdd';
    const LIFECYCLE_CATEGORY = 'c3b3fa78-7a02-45fc-a4a4-5653aa5508b5';
    const ACCESS_CATEGORY = 'eed2e292-abe3-457e-a24f-f8bba5d4ce91';
    const getSystemCategoryWithFallback = (id) => { var _a; return ((_a = categories.get(id)) === null || _a === void 0 ? void 0 : _a.id) || categories.defaultCategory().id; };
    /**
     * List of all available system filters like `updatedAt`, `categoryId`.
     * These are static and not possible for the user to modify in any way.
     */
    const systemFilters = [
        {
            id: 'createdByUserId',
            component: ContractCreatorFilter,
            name: contractCreatorFilterLabel(),
            type: 'system',
            exclude: false,
            categoryId: getSystemCategoryWithFallback(DOCUMENT_DETAILS_CATEGORY),
        },
        {
            id: 'categoryId',
            component: TemplateCategoryFilter,
            name: templateCategoryFilterLabel(),
            type: 'system',
            exclude: false,
            categoryId: getSystemCategoryWithFallback(DOCUMENT_DETAILS_CATEGORY),
        },
        {
            id: 'metadataDocumentType',
            component: SearchDocumentTypeFilter,
            name: searchDocumentTypeFilterLabel(),
            type: 'system',
            exclude: false,
            categoryId: getSystemCategoryWithFallback(DOCUMENT_DETAILS_CATEGORY),
        },
        {
            id: 'metadataDocumentStatus',
            component: DocumentStatusFilter,
            name: documentStatusFilterLabel(),
            type: 'system',
            exclude: false,
            categoryId: getSystemCategoryWithFallback(LIFECYCLE_CATEGORY),
        },
        {
            id: 'createdAt',
            component: CreatedAtFilter,
            name: createdAtFilterLabel(),
            type: 'system',
            exclude: false,
            categoryId: getSystemCategoryWithFallback(DOCUMENT_DETAILS_CATEGORY),
        },
        {
            id: 'updatedAt',
            component: UpdatedAtFilter,
            name: updatedAtFilterLabel(),
            type: 'system',
            exclude: false,
            categoryId: getSystemCategoryWithFallback(DOCUMENT_DETAILS_CATEGORY),
        },
        {
            id: 'metadataDocumentDateSigned',
            component: DateSignedFilter,
            name: dateSignedFilterLabel(),
            type: 'system',
            exclude: false,
            categoryId: getSystemCategoryWithFallback(LIFECYCLE_CATEGORY),
        },
        {
            id: 'metadataDocumentEsigningStatus',
            component: EsigningStatusFilter,
            name: esigningStatusFilterLabel(),
            type: 'system',
            exclude: false,
            categoryId: getSystemCategoryWithFallback(LIFECYCLE_CATEGORY),
        },
        {
            id: 'metadataDocumentIsSharedExternally',
            component: SharedExternallyFilter,
            name: sharedExternallyFilterLabel(),
            type: 'system',
            exclude: false,
            categoryId: getSystemCategoryWithFallback(DOCUMENT_DETAILS_CATEGORY),
        },
        {
            id: 'metadataDocumentProjectIds',
            component: DocumentProjectsFilter,
            name: documentProjectsFilterLabel(),
            type: 'system',
            exclude: !isProjectsAvailable,
            categoryId: getSystemCategoryWithFallback(DOCUMENT_DETAILS_CATEGORY),
        },
        {
            id: 'metadataEntryPath',
            component: FilesystemPathFilter,
            name: fileSystemPathFilterLabel(),
            type: 'system',
            exclude: true,
        },
        {
            id: 'allowedUsers',
            component: AllowedUsersFilter,
            name: allowedUsersFilterLabel(),
            type: 'system',
            exclude: false,
            categoryId: getSystemCategoryWithFallback(ACCESS_CATEGORY),
        },
        {
            id: 'allowedTeams',
            component: AllowedTeamsFilter,
            name: allowedTeamsFilterLabel(),
            type: 'system',
            exclude: false,
            categoryId: getSystemCategoryWithFallback(ACCESS_CATEGORY),
        },
    ];
    // Add all metadata properties as filters, both common and custom properties
    const filterItems = (documentMetadataProperties === null || documentMetadataProperties === void 0 ? void 0 : documentMetadataProperties.reduce((acc, property) => {
        const { isHidden, companyId, categoryId } = property;
        const isCustom = Boolean(companyId === null || companyId === void 0 ? void 0 : companyId.length);
        const FilterComponent = getMetadataPropertyFilter({
            name: property.name,
            searchName: dmsToSearchName(property.name),
            label: property.label,
            type: property.type,
            reference: property.reference,
            allowedValues: property.allowedValues,
        });
        // Some metadata properties are not treated as filters, and will not have any filter component.
        // For example text properties, they are search modifiers and not handled here.
        if (!FilterComponent) {
            return acc;
        }
        const filterItem = {
            id: dmsToSearchName(property.name),
            name: property.label,
            type: isCustom ? 'custom' : 'common',
            component: () => FilterComponent,
            exclude: isHidden,
            categoryId: categoryId !== null && categoryId !== void 0 ? categoryId : categories.defaultCategory().id,
        };
        return [...acc, filterItem];
    }, [...systemFilters])) || [];
    return {
        filterGroups: metadataCategories || [],
        filterItems,
        loading,
        error,
    };
}
